import type { Audience_Insert_Input, AudienceSegmentEntry, NormalizedDefinitionSegment } from '@openx/types/gqlTypes';
import { NormalizedDefinitionOperator } from '@openx/types/gqlTypes';
import { audienceExportType } from '../constants';
import {
  createNormalizedDefinitionFromSegmentSetAndSelection,
  sanitizeNormalizedDefinitionForPricingQuery,
  stripTypename,
} from './normalizedDefinitionFromAudienceItems';
import {
  type AudienceCreateNormalizedDefinition,
  type BuildDealPromptAudienceInsertInputArgs,
  type DealPromptAudienceDealFormValues,
  type DefinitionGroup,
  EXPORT_TARGET_KEYS,
  GROUP_KEYS_FOR_SEGMENTS,
} from './types';

const audienceEntryToCriteriaSegment = (entry: AudienceSegmentEntry): Record<string, unknown> => {
  return {
    id: entry.id,
    name: entry.name,
    category: entry.category,
    description: entry.description ?? '',
    segment_category: entry.segment_category,
    segment_subcategory: entry.segment_subcategory,
    segment_type: entry.segment_type,
    provider_id: entry.account_id,
    account_id: entry.account_id,
    sellable: entry.sellable,
  };
};

const mapSegmentsWithEntries = (segments: NormalizedDefinitionSegment[], byId: Map<string, AudienceSegmentEntry>) =>
  segments.map(seg => {
    const entry = byId.get(String(seg.id));
    if (!entry) {
      return seg;
    }
    return { ...seg, ...audienceEntryToCriteriaSegment(entry) };
  });

const enrichGroup = (group: DefinitionGroup, byId: Map<string, AudienceSegmentEntry>): DefinitionGroup => {
  let result: DefinitionGroup = { ...group };

  for (const key of GROUP_KEYS_FOR_SEGMENTS) {
    const bucket = result[key];
    if (!bucket?.segments?.length) {
      continue;
    }

    const updated = {
      ...bucket,
      segments: mapSegmentsWithEntries(bucket.segments, byId),
    };

    result = { ...result, [key]: updated };
  }

  return result;
};

const enrichDefinitionWithSegmentEntries = (
  definition: AudienceCreateNormalizedDefinition,
  entries: AudienceSegmentEntry[]
): AudienceCreateNormalizedDefinition => {
  const byId = new Map(entries.map(e => [String(e.id), e]));

  return {
    ...definition,
    includes: {
      ...definition.includes,
      groups: definition.includes.groups.map(g => enrichGroup(g, byId)),
    },
  };
};

export const collectSelectedExportTargets = (
  exportTargets: DealPromptAudienceDealFormValues['export_targets']
): string[] => {
  return EXPORT_TARGET_KEYS.reduce<string[]>((acc, key) => {
    if (exportTargets[key]) {
      acc.push(key);
    }
    return acc;
  }, []);
};

export const buildDealPromptAudienceInsertInput = ({
  userId,
  accountId,
  form,
  segmentSet,
  selectedSegments,
}: BuildDealPromptAudienceInsertInputArgs): Audience_Insert_Input => {
  const includesOnly = createNormalizedDefinitionFromSegmentSetAndSelection(segmentSet, selectedSegments);

  const withExcludes: AudienceCreateNormalizedDefinition = {
    includes: includesOnly.includes,
    excludes: {
      operator: NormalizedDefinitionOperator.Intersect,
      groups: [],
    },
  };

  const enriched = enrichDefinitionWithSegmentEntries(withExcludes, selectedSegments);
  const includesWithoutNullCategories = sanitizeNormalizedDefinitionForPricingQuery({
    includes: enriched.includes,
  }).includes;

  const normalized_definition: Audience_Insert_Input['normalized_definition'] = stripTypename({
    includes: includesWithoutNullCategories,
    excludes: enriched.excludes,
  });

  return {
    account_id: accountId,
    user_id: userId,
    name: form.name,
    description: '',
    /** For now, we're using the default export type  */
    export_type: audienceExportType.OA_MATCH,
    export_targets: collectSelectedExportTargets(form.export_targets),
    normalized_definition,
    audience_segments: {
      data: selectedSegments.map(s => ({ segment_id: s.id })),
    },
  };
};
